import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { Answer, AnswerStatus, BidOutcome } from "../types";
import { sanitizeForFirestore } from "../utils/firestoreUtils";

const COLLECTION_NAME = "answers";

export interface FreshnessInfo {
  isOverdue: boolean;
  isDueSoon: boolean;
  daysUntilDue: number;
  badgeLabel: string;
  badgeColor: string;
}

/**
 * Calculates freshness metrics for an answer based on its reviewDueDate
 */
export function calculateFreshness(
  reviewDueDate: string,
  currentStatus: AnswerStatus,
): FreshnessInfo {
  if (currentStatus === "retired") {
    return {
      isOverdue: false,
      isDueSoon: false,
      daysUntilDue: 0,
      badgeLabel: "RETIRED",
      badgeColor: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const due = new Date(reviewDueDate);
  due.setHours(0, 0, 0, 0);

  const diffTime = due.getTime() - today.getTime();
  const daysUntilDue = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (daysUntilDue < 0) {
    const overdueBy = Math.abs(daysUntilDue);
    return {
      isOverdue: true,
      isDueSoon: true,
      daysUntilDue,
      badgeLabel: `OVERDUE (${overdueBy}D)`,
      badgeColor: "bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300",
    };
  }

  if (daysUntilDue <= 30) {
    return {
      isOverdue: false,
      isDueSoon: true,
      daysUntilDue,
      badgeLabel: `REVIEW DUE (${daysUntilDue}D)`,
      badgeColor: "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300",
    };
  }

  return {
    isOverdue: false,
    isDueSoon: false,
    daysUntilDue,
    badgeLabel: "VERIFIED",
    badgeColor: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300",
  };
}

/**
 * Subscribes to the live Approved Answer Library collection in mlc-proposal-hq-db
 */
export function subscribeToAnswers(
  callback: (answers: Answer[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, COLLECTION_NAME);
  const q = query(colRef, orderBy("updatedAt", "desc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const items: Answer[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          title: String(data.title || ""),
          body: String(data.body || ""),
          serviceTags: Array.isArray(data.serviceTags) ? data.serviceTags : [],
          owner: String(data.owner || "Unassigned"),
          ownerEmail: String(data.ownerEmail || ""),
          lastVerifiedDate: String(data.lastVerifiedDate || ""),
          reviewDueDate: String(data.reviewDueDate || ""),
          status: (data.status as AnswerStatus) || "active",
          sourceProposal: data.sourceProposal ? String(data.sourceProposal) : undefined,
          wonBidCount: typeof data.wonBidCount === "number" ? data.wonBidCount : undefined,
          wonBidIds: Array.isArray(data.wonBidIds) ? (data.wonBidIds as string[]) : undefined,
          sourceOpportunityId: data.sourceOpportunityId
            ? String(data.sourceOpportunityId)
            : undefined,
          sourceOutcome: data.sourceOutcome
            ? (String(data.sourceOutcome) as BidOutcome)
            : undefined,
          harvestedFrom: data.harvestedFrom || undefined,
          createdAt: String(data.createdAt || new Date().toISOString()),
          updatedAt: String(data.updatedAt || new Date().toISOString()),
        };
      });
      callback(items);
    },
    (error) => {
      console.error("Error subscribing to answers:", error);
      if (onError) onError(error);
    },
  );
}

/**
 * Creates a new verified answer document in mlc-proposal-hq-db
 */
export async function createAnswer(
  data: Omit<Answer, "id" | "createdAt" | "updatedAt">,
): Promise<string> {
  const now = new Date().toISOString();
  const colRef = collection(db, COLLECTION_NAME);

  const cleanData = sanitizeForFirestore({
    ...data,
    createdAt: now,
    updatedAt: now,
  });

  const docRef = await addDoc(colRef, cleanData);

  return docRef.id;
}

/**
 * Updates an existing answer
 */
export async function updateAnswer(
  id: string,
  updates: Partial<Omit<Answer, "id" | "createdAt">>,
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const cleanUpdates = sanitizeForFirestore({
    ...updates,
    updatedAt: new Date().toISOString(),
  });
  await updateDoc(docRef, cleanUpdates);
}

/**
 * Bumps the lastVerifiedDate to today and extends reviewDueDate by interval days
 */
export async function verifyAnswerFreshness(id: string, intervalDays = 180): Promise<void> {
  const today = new Date();
  const lastVerifiedDate = today.toISOString().slice(0, 10);
  const reviewDueDate = new Date(today.getTime() + intervalDays * 86400000)
    .toISOString()
    .slice(0, 10);

  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    lastVerifiedDate,
    reviewDueDate,
    status: "active",
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Marks an answer as retired (soft retirement)
 */
export async function retireAnswer(id: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    status: "retired",
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Restores a retired answer to active
 */
export async function restoreAnswer(id: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    status: "active",
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Permanently deletes an answer
 */
export async function deleteAnswer(id: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(docRef);
}
